/**
 * Les données de l'utilisateur : retrouver un compte par ce qu'on en sait,
 * puis ouvrir sa fiche — où se lisent ses coordonnées, et d'où l'on modifie
 * son adresse.
 *
 * Lecture seule. La modification passe par la fiche (`/admin/users/:id/
 * edition`) et ses règles (`lib/utilisateurs/fiche.ts`), jamais par cette
 * liste. Aucun chiffre financier ici : ils sont dans la seconde tuile.
 *
 * La recherche vit dans l'adresse (`?q=`) : la tuile de l'accueil l'y pose,
 * et le retour depuis une fiche la retrouve.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { AlertTriangle, ArrowLeft, ArrowRight, Loader2, Pencil, Search, Users } from 'lucide-react';
import { CHEMINS, editionUtilisateur, ficheUtilisateur } from '../../lib/navigation/chemins';
import { chercherDesComptes, type CompteTrouve } from '../../lib/utilisateurs/fiche.supabase';
import { nomAffiche } from '../../lib/utilisateurs/fiche';
import { messageErreur } from '../../lib/payments/erreur';

const PAR_PAGE = 25;

const TYPES: Record<CompteTrouve['accountType'], string> = {
  parent: 'Parent',
  organisation: 'Organisation',
  other: 'Autre',
};

export default function AdminUtilisateursDonneesPage() {
  const [parametres, setParametres] = useSearchParams();
  const q = parametres.get('q') ?? '';
  const [saisie, setSaisie] = useState(q);
  const [page, setPage] = useState(1);
  const [comptes, setComptes] = useState<CompteTrouve[]>([]);
  const [total, setTotal] = useState(0);
  const [pages, setPages] = useState(1);
  const [chargement, setChargement] = useState(false);
  const [erreur, setErreur] = useState<string | null>(null);

  // L'adresse change d'ailleurs (retour arrière, lien) : le champ la suit.
  useEffect(() => {
    setSaisie((s) => (s.trim() === q.trim() ? s : q));
    setPage(1);
  }, [q]);

  // La frappe s'arrête, puis l'adresse change : une requête par recherche,
  // pas une par lettre.
  useEffect(() => {
    const t = setTimeout(() => {
      if (saisie.trim() === q.trim()) return;
      setPage(1);
      setParametres(saisie.trim() === '' ? {} : { q: saisie.trim() }, { replace: true });
    }, 350);
    return () => clearTimeout(t);
  }, [saisie, q, setParametres]);

  // Seule la dernière requête lancée a le droit d'écrire l'affichage.
  const derniere = useRef(0);
  const charger = useCallback(async () => {
    const mienne = ++derniere.current;
    setChargement(true);
    setErreur(null);
    try {
      const r = await chercherDesComptes({ recherche: q, page, parPage: PAR_PAGE });
      if (mienne !== derniere.current) return;
      setComptes(r.comptes);
      setTotal(r.total);
      setPages(r.pages);
    } catch (e) {
      if (mienne === derniere.current) setErreur(messageErreur(e, 'Recherche impossible'));
    } finally {
      if (mienne === derniere.current) setChargement(false);
    }
  }, [q, page]);

  useEffect(() => { void charger(); }, [charger]);

  return (
    <div className="space-y-6">
      <Link to={CHEMINS.utilisateurs.accueil} className="inline-flex items-center gap-1.5 text-sm text-gray-600 hover:text-gray-900">
        <ArrowLeft className="h-4 w-4" /> Utilisateurs
      </Link>

      <div>
        <h1 className="text-2xl font-bold">Données de l’utilisateur</h1>
        <p className="text-gray-600">Retrouver un compte, ouvrir sa fiche, modifier ses coordonnées.</p>
      </div>

      <div className="rounded-xl border border-gray-200 bg-white p-4 shadow-sm">
        <label className="mb-1 block text-xs font-medium text-gray-600" htmlFor="recherche-comptes">Chercher</label>
        <div className="relative">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
          <input
            id="recherche-comptes"
            value={saisie}
            onChange={(e) => setSaisie(e.target.value)}
            placeholder="Nom, prénom, e-mail, organisation, localité, téléphone"
            className="form-input w-full pl-9"
          />
        </div>
        <p className="mt-3 text-xs text-gray-500">
          {chargement ? 'Chargement…' : `${total.toLocaleString('fr-BE')} compte${total > 1 ? 's' : ''} — page ${page} sur ${pages}`}
        </p>
      </div>

      {erreur && (
        <div className="flex gap-2 rounded-lg border border-red-200 bg-red-50 p-4 text-sm text-red-800">
          <AlertTriangle className="h-5 w-5 shrink-0" />
          {erreur}
        </div>
      )}

      <div className="overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm">
        {chargement && comptes.length === 0 && (
          <div className="flex items-center justify-center p-10 text-gray-600">
            <Loader2 className="mr-2 h-5 w-5 animate-spin" /> Chargement des comptes…
          </div>
        )}
        {!chargement && comptes.length === 0 && !erreur && (
          <div className="p-10 text-center text-gray-500">
            <Users className="mx-auto mb-3 h-10 w-10 text-gray-300" />
            Aucun compte ne correspond à cette recherche.
          </div>
        )}
        {comptes.length > 0 && (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200 text-sm">
              <thead className="bg-gray-50">
                <tr className="text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                  <th className="px-4 py-3">Compte</th>
                  <th className="px-4 py-3">Téléphone</th>
                  <th className="px-4 py-3">Localité</th>
                  <th className="px-4 py-3">Type</th>
                  <th className="px-4 py-3"><span className="sr-only">Modifier</span></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {comptes.map((c) => (
                  <tr key={c.id} className="hover:bg-gray-50">
                    <td className="px-4 py-3">
                      <Link to={ficheUtilisateur(c.id)}
                            className="block font-medium text-gray-900 underline decoration-dotted underline-offset-2 hover:text-indigo-700 hover:decoration-solid">
                        {nomAffiche(c)}
                      </Link>
                      <span className="block text-xs text-gray-500">{c.email}</span>
                    </td>
                    <td className="px-4 py-3 text-gray-700">{c.telephone || '—'}</td>
                    <td className="px-4 py-3 text-gray-700">{[c.cpostal, c.localite].filter(Boolean).join(' ') || '—'}</td>
                    <td className="px-4 py-3 text-gray-700">
                      {TYPES[c.accountType] ?? c.accountType}
                      {c.role !== 'parent' && <span className="ml-1 text-xs text-gray-500">({c.role})</span>}
                    </td>
                    <td className="px-4 py-3 text-right">
                      <Link to={editionUtilisateur(c.id)}
                            className="inline-flex items-center gap-1 text-xs font-medium text-primary-700 hover:text-primary-900">
                        <Pencil className="h-3.5 w-3.5" /> Modifier
                      </Link>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {pages > 1 && (
          <div className="flex items-center justify-between border-t border-gray-200 px-4 py-3">
            <button onClick={() => setPage((p) => Math.max(1, p - 1))} disabled={page === 1 || chargement}
                    className="btn-outline flex items-center gap-2 disabled:opacity-50">
              <ArrowLeft className="h-4 w-4" /> Précédent
            </button>
            <span className="text-sm text-gray-600">Page {page} sur {pages}</span>
            <button onClick={() => setPage((p) => Math.min(pages, p + 1))} disabled={page === pages || chargement}
                    className="btn-outline flex items-center gap-2 disabled:opacity-50">
              Suivant <ArrowRight className="h-4 w-4" />
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
